import { useEffect, useId, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, UserRound, X } from 'lucide-react';
import { personInitials } from './people';
import './PeopleSelect.css';

export interface PeopleSelectOption {
  id: string;
  name: string;
  photo?: string | null;
}

interface PeopleSelectProps {
  value: string;
  onChange: (id: string) => void;
  options: readonly PeopleSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  clearLabel?: string;
}

export function PersonAvatar({ person }: { person: PeopleSelectOption }) {
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  return (
    <span className="org-person-avatar">
      {person.photo && failedPhoto !== person.photo ? (
        <img
          src={person.photo}
          alt={person.name}
          onError={() => setFailedPhoto(person.photo || null)}
        />
      ) : (
        <span aria-hidden="true">{personInitials(person.name)}</span>
      )}
    </span>
  );
}

export default function PeopleSelect({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = 'Choose a person',
  ariaLabel = placeholder,
  id,
  clearLabel = 'Clear selection',
}: PeopleSelectProps) {
  const textId = useId();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const selected = options.find((person) => person.id === value);
  const selectedName = selected?.name || (value ? 'Unavailable person' : placeholder);
  return (
    <DropdownMenu.Root open={open} onOpenChange={(next) => setOpen(next && !disabled)}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          className="org-people-select-trigger"
          data-people-select-trigger
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={textId}
        >
          {selected ? (
            <PersonAvatar key={selected.id} person={selected} />
          ) : (
            <span className="org-person-avatar" aria-hidden="true">
              <UserRound size={15} />
            </span>
          )}
          <span
            className={
              selected ? 'org-people-select-name' : 'org-people-select-name is-placeholder'
            }
            id={textId}
          >
            {selectedName}
          </span>
          <ChevronDown className="org-people-select-chevron" size={14} aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="org-people-select-menu"
          sideOffset={5}
          align="start"
          collisionPadding={12}
          aria-label={ariaLabel}
        >
          <DropdownMenu.RadioGroup
            value={value}
            onValueChange={(next) => {
              if (!disabled && next !== value) onChange(next);
            }}
          >
            <DropdownMenu.RadioItem
              className="org-people-select-option is-clear"
              value=""
              textValue={clearLabel}
            >
              <span className="org-person-avatar" aria-hidden="true">
                <X size={14} />
              </span>
              <span className="org-people-select-name">{clearLabel}</span>
              <DropdownMenu.ItemIndicator className="org-people-select-check">
                <Check size={14} aria-hidden="true" />
              </DropdownMenu.ItemIndicator>
            </DropdownMenu.RadioItem>
            <DropdownMenu.Separator className="org-people-select-separator" />
            {options.map((person) => (
              <DropdownMenu.RadioItem
                key={person.id}
                className="org-people-select-option"
                value={person.id}
                textValue={person.name}
                aria-label={person.name}
              >
                <PersonAvatar person={person} />
                <span className="org-people-select-name">{person.name}</span>
                <DropdownMenu.ItemIndicator className="org-people-select-check">
                  <Check size={14} aria-hidden="true" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          {!options.length && <p className="org-people-select-empty">No people available</p>}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
